/**
 * Shared error and warning codes for the import pipeline.
 *
 * Kept in sync with the backend's importErrorCodes.ts — if you change codes
 * in one place, change them in the other. Display messages live here because
 * the backend emits codes only; rendering is the frontend's job.
 */

export const IMPORT_ERROR_CODES = {
    // Parse-time (frontend)
    MISSING_HEADERS: 'MISSING_HEADERS',
    MISSING_IDENTIFIERS: 'MISSING_IDENTIFIERS',
    INVALID_NUMBER: 'INVALID_NUMBER',

    // Validation-time (backend)
    UNKNOWN_SCENARIO: 'UNKNOWN_SCENARIO',
    UNKNOWN_ACCOUNT: 'UNKNOWN_ACCOUNT',
    UNKNOWN_PERIOD: 'UNKNOWN_PERIOD',

    // Execute-time
    NO_VALUE_ROW: 'NO_VALUE_ROW',
};

export const IMPORT_WARNING_CODES = {
    BLANK_ROW_WITH_IDENTIFIERS: 'BLANK_ROW_WITH_IDENTIFIERS',
};

export const IMPORT_ERROR_MESSAGES = {
    [IMPORT_ERROR_CODES.MISSING_HEADERS]: (_value, details) => {
        const missing = details?.missing?.join(', ') || 'required headers';
        return `Sheet is missing required headers: ${missing}. The header row must contain a scenario column and an account column.`;
    },
    [IMPORT_ERROR_CODES.MISSING_IDENTIFIERS]: () =>
        `Missing scenario name or account number.`,
    [IMPORT_ERROR_CODES.INVALID_NUMBER]: (value) =>
        `"${value}" is not a valid number.`,
    [IMPORT_ERROR_CODES.UNKNOWN_SCENARIO]: (value) =>
        `Scenario "${value}" was not found.`,
    [IMPORT_ERROR_CODES.UNKNOWN_ACCOUNT]: (value) =>
        `Account "${value}" was not found.`,
    [IMPORT_ERROR_CODES.UNKNOWN_PERIOD]: (value, details) => {
        const base = `Period "${value}" does not exist${details?.scenarioName ? ` for scenario "${details.scenarioName}"` : ''}.`;
        if (details?.suggestion) {
            return `${base} Did you mean "${details.suggestion}"?`;
        }
        return base;
    },
    [IMPORT_ERROR_CODES.NO_VALUE_ROW]: () =>
        `No corresponding value row exists in the database.`,
};

export const IMPORT_WARNING_MESSAGES = {
    [IMPORT_WARNING_CODES.BLANK_ROW_WITH_IDENTIFIERS]: () =>
        `Row has identifiers but no period values to import.`,
};

export function formatImportError(error) {
    const formatter = IMPORT_ERROR_MESSAGES[error.code];
    if (!formatter) return `Unknown error: ${error.code}`;
    return formatter(error.value, error.details);
}

export function formatImportWarning(warning) {
    const formatter = IMPORT_WARNING_MESSAGES[warning.code];
    if (!formatter) return `Unknown warning: ${warning.code}`;
    return formatter(warning.details);
}